import * as React from 'react'
import { Timer, Vote } from 'lucide-react'
import type { BoardVote } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Dot voting: the dialog that opens a round, and the banner that runs it.
 *
 * Both live here rather than in pages/board-canvas.tsx because neither touches
 * the canvas — the banner only needs the vote row and two counts, and keeping
 * the second-by-second countdown in its own component means the whole board
 * doesn't re-render once a second while a vote is running.
 */

/** Seconds left, never negative. */
export function secondsLeft(vote: BoardVote, now: number): number {
  return Math.max(0, Math.round((new Date(vote.ends_at).getTime() - now) / 1000))
}

/**
 * Is this round still taking dots? False once it is closed early or the clock
 * has run out.
 *
 * Deliberately a plain boolean rather than a `vote is BoardVote` type guard:
 * inside the banner, where the vote is already non-null, the negative branch of
 * a guard narrows it to `never` and reading `closed_at` there stops compiling.
 */
export function isVoteOpen(vote: BoardVote | null, now: number): boolean {
  return Boolean(vote && !vote.closed_at && secondsLeft(vote, now) > 0)
}

/** m:ss — the shape of every timer anyone has ever watched run down. */
export function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const DURATIONS = [2, 5, 10, 15]

export function StartVoteDialog({
  onClose,
  onStart,
}: {
  onClose: () => void
  onStart: (opts: { label: string | null; votesPerUser: number; minutes: number }) => Promise<void>
}) {
  const [label, setLabel] = React.useState('')
  const [votesPerUser, setVotesPerUser] = React.useState(3)
  const [minutes, setMinutes] = React.useState(5)
  const [busy, setBusy] = React.useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await onStart({ label: label.trim() || null, votesPerUser, minutes })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Start a vote</DialogTitle>
            <DialogDescription>
              Everyone on the team drops dots on the board. The dots stay after the clock runs out.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vote-label">What are we deciding?</Label>
              <Input
                id="vote-label"
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Which three do we tackle first?"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vote-budget">Votes per person</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="vote-budget"
                  type="number"
                  min={1}
                  max={20}
                  className="w-20"
                  value={votesPerUser}
                  onChange={(e) => setVotesPerUser(clampInt(e.target.value, 1, 20, 3))}
                />
                {/* The three everyone actually uses, one click away. The field
                    stays because a facilitator with eleven options wants four. */}
                {[3, 5].map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant={votesPerUser === n ? 'default' : 'outline'}
                    onClick={() => setVotesPerUser(n)}
                  >
                    {n}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>How long?</Label>
              <div className="flex flex-wrap items-center gap-2">
                {DURATIONS.map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant={minutes === n ? 'default' : 'outline'}
                    onClick={() => setMinutes(n)}
                  >
                    {n} min
                  </Button>
                ))}
                <Input
                  type="number"
                  min={1}
                  max={120}
                  className="w-20"
                  value={minutes}
                  onChange={(e) => setMinutes(clampInt(e.target.value, 1, 120, 5))}
                  aria-label="Minutes"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                You can end it early. When the clock runs out the dots stay on the board.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              <Vote /> Start voting
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The live banner. Everyone sees the clock and their own remaining dots; only
 * the facilitator gets the buttons.
 *
 * `now` ticks here, not in the board, so a running vote costs one small
 * re-render a second instead of re-rendering every object on the canvas.
 */
export function VoteBanner({
  vote,
  used,
  total,
  isCoach,
  onEnd,
  onClear,
}: {
  vote: BoardVote
  used: number
  total: number
  isCoach: boolean
  onEnd: () => void
  onClear: () => void
}) {
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [])

  const open = isVoteOpen(vote, now)
  const left = secondsLeft(vote, now)
  const remaining = Math.max(0, vote.votes_per_user - used)

  return (
    <div
      className={cn(
        'pointer-events-auto absolute left-1/2 top-3 z-30 flex max-w-[min(92vw,44rem)] -translate-x-1/2 items-center gap-3 rounded-lg border px-3 py-2 shadow-md backdrop-blur',
        open ? 'border-primary/40 bg-background/95' : 'bg-background/95',
      )}
    >
      <Vote className={cn('h-4 w-4 shrink-0', open ? 'text-primary' : 'text-muted-foreground')} />

      <div className="min-w-0">
        <div className="truncate text-sm font-medium leading-tight">
          {vote.label || (open ? 'Voting' : 'Voting closed')}
        </div>
        <div className="truncate text-[11px] leading-tight text-muted-foreground">
          {open ? (
            <>
              {remaining === 0
                ? 'All your votes are in — click one of yours to take it back'
                : `${remaining} of ${vote.votes_per_user} left — click the board to drop one`}
              {' · '}
              {total} {total === 1 ? 'dot' : 'dots'} so far
            </>
          ) : (
            <>
              {total} {total === 1 ? 'dot' : 'dots'} · {vote.closed_at ? 'ended early' : 'time is up'}
            </>
          )}
        </div>
      </div>

      {open && (
        <span className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums">
          <Timer className="h-3 w-3" />
          {formatClock(left)}
        </span>
      )}

      {isCoach && (
        <div className="ml-1 flex shrink-0 items-center gap-1">
          {open ? (
            <Button size="sm" variant="outline" onClick={onEnd}>
              End now
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onClear}>
              Clear
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/** Parse a number input that a person can empty or fill with nonsense. */
function clampInt(raw: string, lo: number, hi: number, fallback: number): number {
  const n = Number.parseInt(raw, 10)
  if (Number.isNaN(n)) return fallback
  return Math.min(hi, Math.max(lo, n))
}
